import { ButtonGroup, IconButton } from '@cad-arc';
import React from 'react';
import { useEnabledFeatures } from '../../../common/hooks/enabled-features/use-enabled-features';
import { AdditionalInfoType, AdditionalInfoView, Priority, PRO_QA_TYPES } from '../../types';

export interface NarrativeActionMenuProps {
    info: AdditionalInfoView;
    currentUserId?: number;
    isPinned?: boolean;
    isScheduledEvent?: boolean;
    isFirstResponder?: boolean;
    onEdit?: () => void;
    onEmphasize?: () => void;
    onRemoveEmphasis?: () => void;
    onPin?: () => void;
    onUnpin?: () => void;
    onScrollTo?: () => void;
    onDelete?: () => void;
    isDeleting?: boolean;
}

export function NarrativeActionMenu({
    info,
    currentUserId,
    isPinned = false,
    isScheduledEvent = false,
    isFirstResponder = false,
    onEdit,
    onEmphasize,
    onRemoveEmphasis,
    onPin,
    onUnpin,
    onScrollTo,
    onDelete,
    isDeleting = false,
}: NarrativeActionMenuProps): React.JSX.Element | null {
    const {
        rollOutCadNarrativeFilteringAndFocusImprovementsEnabled,
        editAdditionalInfos,
        pinUnpinAdditionalInfos,
        deleteAdditionalInfos,
    } = useEnabledFeatures();

    if (!rollOutCadNarrativeFilteringAndFocusImprovementsEnabled) {
        return null;
    }

    const canEdit = editAdditionalInfos ?? false;
    const canPinUnpin = pinUnpinAdditionalInfos ?? false;
    // Default true to match existing behavior in event-panel-additional-infos.tsx
    // and bolo-panel-additional-infos.tsx
    const canDelete = deleteAdditionalInfos ?? true;

    const isAuthor = currentUserId != null && currentUserId === info.authorUserId;
    const isUserType = info.type === AdditionalInfoType.USER;
    const isProQA = PRO_QA_TYPES.includes(info.type as AdditionalInfoType);
    const isHighPriority = info.priority === Priority.HIGH;
    const isStandardPriority = info.priority === Priority.STANDARD;

    const showScrollTo = isPinned;
    const showEdit = canEdit && isAuthor && isUserType;
    const showEmphasize = canEdit && isAuthor && isUserType && isStandardPriority;
    const showRemoveEmphasis = canEdit && isAuthor && isUserType && isHighPriority;
    const showPin = canPinUnpin && !isPinned;
    const showUnpin = canPinUnpin && isPinned;
    const showDelete =
        canDelete &&
        isAuthor &&
        isUserType &&
        !isScheduledEvent &&
        !info.isEventNarrative &&
        !isProQA;

    const hasAnyAction =
        showScrollTo ||
        showEdit ||
        showEmphasize ||
        showRemoveEmphasis ||
        showPin ||
        showUnpin ||
        showDelete;

    if (!hasAnyAction) {
        return null;
    }

    const buttonSize = isFirstResponder ? 'lg' : 'sm';

    return (
        <ButtonGroup
            isAttached
            className="narrative-action-menu"
            role="toolbar"
            aria-label="Narrative actions"
            data-testid="NARRATIVE_ACTION_MENU"
        >
            {showScrollTo && (
                <IconButton
                    aria-label="Scroll to"
                    data-testid="NARRATIVE_ACTION_SCROLL_TO"
                    icon="Crosshairs"
                    size={buttonSize}
                    variant="ghost"
                    onClick={onScrollTo}
                />
            )}
            {showEdit && (
                <IconButton
                    aria-label="Edit"
                    data-testid="NARRATIVE_ACTION_EDIT"
                    icon="Edit"
                    size={buttonSize}
                    variant="ghost"
                    onClick={onEdit}
                />
            )}
            {showEmphasize && (
                <IconButton
                    aria-label="Emphasize"
                    data-testid="NARRATIVE_ACTION_EMPHASIZE"
                    icon="ExclamationPoint"
                    size={buttonSize}
                    variant="ghost"
                    onClick={onEmphasize}
                />
            )}
            {showRemoveEmphasis && (
                <IconButton
                    aria-label="Remove emphasis"
                    data-testid="NARRATIVE_ACTION_REMOVE_EMPHASIS"
                    icon="ExclamationPointOff"
                    size={buttonSize}
                    variant="ghost"
                    onClick={onRemoveEmphasis}
                />
            )}
            {showPin && (
                <IconButton
                    aria-label="Pin"
                    data-testid="NARRATIVE_ACTION_PIN"
                    icon="PinOutline"
                    size={buttonSize}
                    variant="ghost"
                    onClick={onPin}
                />
            )}
            {showUnpin && (
                <IconButton
                    aria-label="Unpin"
                    data-testid="NARRATIVE_ACTION_UNPIN"
                    icon="PinOutlineOff"
                    size={buttonSize}
                    variant="ghost"
                    onClick={onUnpin}
                />
            )}
            {showDelete && (
                <IconButton
                    aria-label="Delete"
                    data-testid="NARRATIVE_ACTION_DELETE"
                    icon="TrashCan"
                    size={buttonSize}
                    variant="ghost"
                    onClick={onDelete}
                    isDisabled={isDeleting}
                />
            )}
        </ButtonGroup>
    );
}
